import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/*
  Scan de code-barres pour le comptage d'inventaire — pensé ANDROID d'abord.

  POURQUOI UN HOOK ET PAS UN COMPOSANT : le plugin ML Kit rend la WebView
  TRANSPARENTE et affiche la caméra DERRIÈRE elle. Il ne s'agit donc pas
  d'insérer une vidéo dans la page, mais de faire disparaître la page le temps
  du scan — c'est une bascule d'état global, pas un bout d'interface. D'où la
  classe posée sur <body> (`scan-actif`), que la CSS utilise pour vider l'écran.

  DÉGRADATION : sur le web (et sur une APK sans module ML Kit installé), le scan
  n'existe pas. Le hook le dit (`disponible: false`) et l'écran propose la
  saisie manuelle du code — jamais un bouton qui ne fait rien.

  L'import du plugin est PARESSEUX : sur le web, importer un plugin natif au
  chargement du module ferait échouer le bundle pour une fonction qui n'y sert
  pas.
*/

const estNatif = () => {
  try { return Capacitor.isNativePlatform(); } catch (e) { return false; }
};

export default function useScanCode() {
  const [disponible, setDisponible] = useState(false);
  const [actif, setActif] = useState(false);
  const [torche, setTorche] = useState(false);
  const plugin = useRef(null);
  const ecouteur = useRef(null);

  const charger = useCallback(async () => {
    if (plugin.current) return plugin.current;
    if (!estNatif()) return null;
    try {
      const mod = await import('@capacitor-mlkit/barcode-scanning');
      plugin.current = mod.BarcodeScanner;
      return plugin.current;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    let vivant = true;
    (async () => {
      const p = await charger();
      if (!vivant || !p) { if (vivant) setDisponible(false); return; }
      try {
        // Sur certains Android, le module ML Kit se télécharge à la demande :
        // on le vérifie AVANT de proposer le scan, sinon le premier appui
        // échoue sans explication.
        const { available } = await p.isGoogleBarcodeScannerModuleAvailable();
        if (!available) {
          try { await p.installGoogleBarcodeScannerModule(); } catch (e) { /* réessaiera */ }
        }
        if (vivant) setDisponible(true);
      } catch (e) {
        if (vivant) setDisponible(false);
      }
    })();
    return () => { vivant = false; };
  }, [charger]);

  const arreter = useCallback(async () => {
    const p = plugin.current;
    setActif(false);
    setTorche(false);
    document.body.classList.remove('scan-actif');
    if (!p) return;
    try { if (ecouteur.current) { await ecouteur.current.remove(); ecouteur.current = null; } } catch (e) { /* ignore */ }
    try { await p.stopScan(); } catch (e) { /* ignore */ }
  }, []);

  /**
   * Démarre le scan. `onCode` est appelé à CHAQUE lecture — l'appelant décide
   * s'il continue (comptage en rafale) ou s'il arrête.
   */
  const scanner = useCallback(async (onCode) => {
    const p = await charger();
    if (!p) return { ok: false, raison: 'indisponible' };
    try {
      const { camera } = await p.requestPermissions();
      if (camera !== 'granted' && camera !== 'limited') {
        return { ok: false, raison: 'permission' };
      }
      document.body.classList.add('scan-actif');
      setActif(true);
      ecouteur.current = await p.addListener('barcodeScanned', async (res) => {
        const valeur = res?.barcode?.displayValue || res?.barcode?.rawValue;
        if (valeur) onCode(String(valeur).trim());
      });
      await p.startScan();
      return { ok: true };
    } catch (e) {
      await arreter();
      return { ok: false, raison: 'erreur', message: e?.message };
    }
  }, [charger, arreter]);

  const basculerTorche = useCallback(async () => {
    const p = plugin.current;
    if (!p) return;
    try {
      if (torche) { await p.disableTorch(); setTorche(false); }
      else { await p.enableTorch(); setTorche(true); }
    } catch (e) { /* certaines caméras n'ont pas de torche */ }
  }, [torche]);

  // Filet : si l'écran est démonté pendant un scan, la caméra doit se fermer et
  // la page redevenir visible — sinon l'app reste transparente.
  useEffect(() => () => {
    document.body.classList.remove('scan-actif');
    const p = plugin.current;
    if (p) { try { p.stopScan(); } catch (e) { /* ignore */ } }
  }, []);

  return { disponible, actif, torche, scanner, arreter, basculerTorche };
}
